import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { Avatar } from '@/components/Avatar';
import { alternarFavorito, favoritos as leerFavoritos } from './accesos';
import { useAvatarPropio } from './api';
import {
  administracionVisible, destinoPara, gruposVisibles, hayAdministracion, INICIO,
  puedeVer, type Destino,
} from './navegacion';

/**
 * El menú lateral.
 *
 * ## Lo que cambia
 *
 * - **La visibilidad se decide una vez, con el dato.** El armazón la resolvía
 *   con cuatro barridos del documento (`querySelectorAll('.nav-item[data-module]')`,
 *   `.nav-admin`, `.nav-staff`, `.nav-group-staff`) poniendo `style.display` a
 *   mano. Si alguien añadía un destino y olvidaba la clase, se lo veía todo el
 *   mundo — y eso no falla en ninguna parte.
 * - **El favorito no se inyecta en el DOM después.** `injectPinButtons()`
 *   recorría el menú creando un `<button>` por destino y enganchándole su
 *   listener, y había que volver a llamarlo cada vez que el menú se repintaba.
 * - **La estrella dice de qué destino es.** Era un botón con un icono: un
 *   lector de pantalla leía «botón» veinte veces seguidas.
 *
 * ## Lo que NO cambia, a propósito
 *
 * El acordeón se comporta igual: **un grupo abierto a la vez**, se abre al pasar
 * por encima, y al salir vuelve al «grupo pegajoso» —el del destino activo, o el
 * que se fijó con un clic— después de 650 ms. Ese retardo existe para que cruzar
 * el menú con el ratón no lo cierre en la cara.
 *
 * ## Las tres medidas las sigue poniendo el CSS
 *
 * El ancho (`--sidebar-w`), el del riel (`--sidebar-mini`) y el corte
 * (`--breakpoint-menu`, de donde sale la variante `menu:`) se **leen** de
 * `design-system.css`, no se repiten aquí. Ese archivo es quien le da o le quita
 * el margen izquierdo a `.main-content`, así que si los dos lados no valen lo
 * mismo queda una franja vacía del ancho del menú — que es exactamente lo que
 * pasaba con `lg:` (1024) contra el corte real de 900.
 */

const GRUPO_ABIERTO = 'nav_open';
const MINI = 'sidebar_mini';
const RETARDO_CIERRE = 650;

export function MenuLateral({ paginaActiva, hashActivo, abierto, onCerrar }: {
  /** La clave del `PAGE_MAP` de la pantalla que se está viendo. */
  paginaActiva: string;
  /** El hash completo, con sub-ruta. Hace falta porque **dos destinos pueden
   *  compartir página**: «Bases de Datos» y «Recolección de Datos» son los dos
   *  `private-records`, y se distinguen solo por la sub-ruta. Marcando por
   *  página se resaltaría siempre el primero. */
  hashActivo: string;
  /** En móvil el menú es un cajón. */
  abierto: boolean;
  onCerrar: () => void;
}) {
  const grupos = gruposVisibles();
  // El hash EXACTO manda sobre la página, que es justo lo que hacía el armazón
  // con `querySelector('[href="#…"]') || querySelector('[data-page="…"]')`.
  // Misma precedencia y misma función que usa «Recientes», para que el menú
  // marque el mismo destino que se apunta en el historial.
  const activo = destinoPara(paginaActiva, hashActivo)?.hash ?? paginaActiva;
  const admin = administracionVisible();
  const usuario = Auth.getUser();
  const foto = useAvatarPropio().data?.avatar_url;

  const [favoritos, setFavoritos] = useState(leerFavoritos);
  const [mini, setMini] = useState(() => !!Utils.recordar.leer(MINI, false));

  // `body.sidebar-mini` es lo que mueve el contenido: la regla que estrecha el
  // menú y la que le quita margen a `.main-content` cuelgan las dos de esta
  // clase. Sin ponerla, el menú se encogería y el contenido se quedaría donde
  // estaba, con un hueco de 184 px.
  useEffect(() => {
    document.body.classList.toggle('sidebar-mini', mini);
  }, [mini]);

  // El grupo que manda cuando nadie pasa el ratón: el del destino activo, o el
  // que se fijó con un clic.
  const pegajoso = grupos.find((g) => g.destinos.some((d) => d.hash === activo))?.clave
    ?? Utils.recordar.leer<string>(GRUPO_ABIERTO, '')
    ?? '';
  const [expandido, setExpandido] = useState<string>(pegajoso || grupos[0]?.clave || '');
  const temporizador = useRef<number | undefined>(undefined);

  // Al cambiar de pantalla, el menú sigue a lo que se está viendo.
  useEffect(() => {
    if (pegajoso) setExpandido(pegajoso);
  }, [pegajoso]);

  useEffect(() => () => window.clearTimeout(temporizador.current), []);

  function alEntrar(clave: string) {
    window.clearTimeout(temporizador.current);
    setExpandido(clave);
  }

  function alSalir() {
    window.clearTimeout(temporizador.current);
    temporizador.current = window.setTimeout(() => {
      setExpandido(pegajoso || '');
    }, RETARDO_CIERRE);
  }

  function alternarGrupo(clave: string) {
    window.clearTimeout(temporizador.current);
    const fijado = Utils.recordar.leer<string>(GRUPO_ABIERTO, '');
    if (expandido === clave && fijado === clave) {
      setExpandido('');
      Utils.recordar.guardar(GRUPO_ABIERTO, '');
    } else {
      setExpandido(clave);
      Utils.recordar.guardar(GRUPO_ABIERTO, clave);
    }
  }

  function fijar(d: Destino, grupo: string) {
    const nuevos = alternarFavorito(d, grupo);
    if (nuevos) setFavoritos(nuevos);   // `null` = no cabía, y ya lo dijo
  }

  const esFavorito = (d: Destino) => favoritos.some((f) => f.href === `#${d.hash}`);

  return (
    <>
      {/* El velo del cajón en móvil. */}
      {abierto && (
        <button
          type="button"
          className="fixed inset-0 z-armazon bg-ink/40 menu:hidden"
          onClick={onCerrar}
          aria-label="Cerrar el menú"
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-menu flex flex-col bg-brand text-on-brand',
          'transition-[width,transform] duration-250 ease-out-soft',
          mini ? 'w-[var(--sidebar-mini)]' : 'w-[var(--sidebar-w)]',
          abierto ? 'translate-x-0' : '-translate-x-full menu:translate-x-0',
        )}
        aria-label="Menú principal"
      >
        <div className="flex items-center gap-2 p-3">
          <Marca mini={mini} />
          <button
            type="button"
            className="hidden size-8 shrink-0 items-center justify-center rounded-sm hover:bg-surface/10 focus-visible:focus-anillo menu:flex"
            onClick={() => setMini((v) => { Utils.recordar.guardar(MINI, !v); return !v; })}
            aria-label={mini ? 'Expandir el menú' : 'Contraer el menú'}
            aria-pressed={mini}
          >
            <i className={`bi bi-chevron-double-${mini ? 'right' : 'left'}`} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="flex size-8 shrink-0 items-center justify-center rounded-sm hover:bg-surface/10 focus-visible:focus-anillo menu:hidden"
            onClick={onCerrar}
            aria-label="Cerrar el menú"
          >
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </div>

        {/* La tarjeta de usuario es un atajo a Mi perfil, que es lo que se
            espera al pulsar el propio avatar. Dejó de desplegar un menú el
            2026-07-30, cuando «Cerrar sesión» pasó al pie. */}
        <a
          href="#perfil"
          className="flex items-center gap-2 px-3 py-2 text-on-brand no-underline hover:bg-surface/10 focus-visible:focus-anillo"
          title="Mi perfil"
        >
          <Avatar nombre={usuario?.full_name ?? ''} foto={foto} tamano={mini ? 28 : 36} />
          {!mini && (
            <span className="min-w-0 flex-1">
              <span className="block truncate text-3 font-semibold">{usuario?.full_name || 'Usuario'}</span>
              <span className="block truncate text-1 opacity-75">{usuario?.role || ''}</span>
            </span>
          )}
          {!mini && <i className="bi bi-chevron-right shrink-0 opacity-60" aria-hidden="true" />}
        </a>

        {/* El scroll es de la lista, no del <aside>: así la marca y la tarjeta
            de usuario se quedan fijas arriba y «Cerrar sesión» abajo. */}
        <nav className="min-h-0 flex-1 overflow-y-auto px-2 pb-4">
          <Enlace destino={INICIO} activo={activo} mini={mini}
            favorito={esFavorito(INICIO)} onFavorito={() => fijar(INICIO, '')} />

          {!mini && <Rotulo>Módulos</Rotulo>}

          {grupos.map((g) => {
            const desplegado = expandido === g.clave;
            return (
              <div key={g.clave} onMouseEnter={() => alEntrar(g.clave)} onMouseLeave={alSalir}>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-3 hover:bg-surface/10 focus-visible:focus-anillo"
                  onClick={() => alternarGrupo(g.clave)}
                  aria-expanded={desplegado}
                  title={g.etiqueta}
                >
                  <i className={`bi ${g.icono} shrink-0`} aria-hidden="true" />
                  {!mini && <span className="min-w-0 flex-1 truncate text-left">{g.etiqueta}</span>}
                  {!mini && (
                    <i
                      className={cn('bi bi-chevron-down shrink-0 transition-transform', desplegado && 'rotate-180')}
                      aria-hidden="true"
                    />
                  )}
                </button>
                {desplegado && (
                  <div className={cn(!mini && 'ml-2 border-l border-surface/20 pl-1')}>
                    {g.destinos.map((d) => (
                      <Enlace
                        key={d.hash}
                        destino={d}
                        activo={activo}
                        mini={mini}
                        favorito={esFavorito(d)}
                        onFavorito={() => fijar(d, g.clave)}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {hayAdministracion() && admin.length > 0 && (
            <>
              {!mini && <Rotulo>Administración</Rotulo>}
              {admin.map((d) => (
                <Enlace
                  key={d.hash}
                  destino={d}
                  activo={activo}
                  mini={mini}
                  favorito={esFavorito(d)}
                  onFavorito={() => fijar(d, 'administracion')}
                />
              ))}
            </>
          )}
        </nav>

        <div className="border-t border-surface/20 p-2">
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-3 hover:bg-surface/10 focus-visible:focus-anillo"
            onClick={() => Auth.logout()}
            title="Cerrar sesión"
          >
            <i className="bi bi-box-arrow-left shrink-0" aria-hidden="true" />
            {!mini && <span>Cerrar sesión</span>}
          </button>
        </div>
      </aside>
    </>
  );
}

function Rotulo({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-2 pb-1 pt-4 text-1 font-bold uppercase tracking-wide opacity-60">{children}</p>
  );
}

/**
 * La marca del portal: el logo de la empresa si lo hay, y si no su nombre.
 *
 * El logo lo metía `applyBranding()` escribiendo `cssText` e `innerHTML` sobre
 * `.logo-mark-sm`. Aquí es un `<img>` normal — pero la **placa blanca** se
 * conserva y sigue siendo `#fff` literal y no `var(--surface)`: el logo lo manda
 * la empresa y viene con tinta oscura, así que sobre el token —que en tema
 * oscuro se invierte— desaparecería sobre su propio fondo.
 */
function Marca({ mini }: { mini: boolean }) {
  const b = window._branding;
  const nombre = b?.portal_name?.replace(/^Portal de /i, '') || 'Bienestar';

  if (b?.logo_url && !mini) {
    return (
      <span
        className="flex h-9 min-w-0 flex-1 items-center justify-center rounded-sm p-1"
        style={{ background: '#fff' }}
      >
        <img src={b.logo_url} alt={b.portal_name || 'Logo'} className="max-h-full max-w-full object-contain" />
      </span>
    );
  }
  return (
    <span className="min-w-0 flex-1 truncate text-4 font-bold">{mini ? nombre.charAt(0) : nombre}</span>
  );
}

/** Un destino, con su estrella. */
function Enlace({ destino: d, activo, mini, favorito, onFavorito }: {
  destino: Destino;
  /** El hash del destino que se está viendo. */
  activo: string;
  mini: boolean;
  favorito: boolean;
  onFavorito: () => void;
}) {
  if (!puedeVer(d)) return null;
  const esta = d.hash === activo;
  return (
    <span className="group relative flex items-center">
      <a
        href={`#${d.hash}`}
        aria-current={esta ? 'page' : undefined}
        title={d.etiqueta}
        className={cn(
          'flex min-w-0 flex-1 items-center gap-2 rounded-sm px-2 py-2 text-3 no-underline',
          'focus-visible:focus-anillo',
          esta ? 'bg-surface/15 font-semibold text-on-brand' : 'text-on-brand/90 hover:bg-surface/10',
        )}
      >
        <i className={`bi ${d.icono} shrink-0`} aria-hidden="true" />
        {!mini && <span className="min-w-0 flex-1 truncate">{d.etiqueta}</span>}
      </a>
      {!mini && (
        <button
          type="button"
          className={cn(
            'absolute right-1 rounded-xs px-1 text-2 focus-visible:focus-anillo',
            favorito ? 'text-accent' : 'opacity-0 group-hover:opacity-70 focus:opacity-100',
          )}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onFavorito(); }}
          // Era un botón con una estrella y nada más: veinte «botón» seguidos.
          aria-label={favorito ? `Quitar ${d.etiqueta} de favoritos` : `Fijar ${d.etiqueta} en favoritos`}
          aria-pressed={favorito}
        >
          <i className={`bi ${favorito ? 'bi-star-fill' : 'bi-star'}`} aria-hidden="true" />
        </button>
      )}
    </span>
  );
}
